import { useState, FormEvent } from 'react';
import RevealOnScroll from '../base/RevealOnScroll';

const FORM_URL = 'https://readdy.ai/api/form/d71vf8k991ove783t7pg';

const serviceOptions = [
  'Make-Ready Remodeling',
  'Flooring',
  'Drywall & Painting',
  'Electrical',
  'Plumbing',
  'Roofing',
  'Multiple Services',
  'Other / Not Sure',
];

const BG_IMAGE = 'https://public.readdy.ai/ai/img_res/53aaf9e0b3cc434a92fda1a51c5b47c6.jpg';

interface Props {
  headline?: string;
  subheadline?: string;
}

export default function FinalCTA({
  headline = 'Ready to Elevate Your Houston Property?',
  subheadline = 'Request your free quote today. No obligation, no pressure — just honest, professional service done right the first time.',
}: Props) {
  const [formState, setFormState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem('message') as HTMLTextAreaElement)?.value || '';
    if (message.length > 500) return;

    setFormState('submitting');
    try {
      const data = new FormData(form);
      const body = new URLSearchParams();
      data.forEach((val, key) => body.append(key, val as string));
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setFormState('success');
        form.reset();
        setCharCount(0);
      } else {
        setFormState('error');
      }
    } catch {
      setFormState('error');
    }
  };

  return (
    <section id="quote" className="relative py-28 lg:py-36 overflow-hidden">
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src={BG_IMAGE}
          alt="One Call Handyman Service Houston TX"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-[#0A0806]/88" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0806]/80 to-[#0A0806]/60" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 xl:gap-24 items-start">

          {/* Left — contact info */}
          <RevealOnScroll direction="left">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-px bg-[#BF9448]" />
              <span className="text-[#BF9448] text-[10px] font-sans font-semibold tracking-[0.25em] uppercase">
                Free Estimate
              </span>
            </div>
            <h2
              className="font-serif font-semibold text-white leading-tight mb-5"
              style={{ fontSize: 'clamp(2rem, 3.5vw, 3rem)' }}
            >
              {headline}
            </h2>
            <p className="font-sans text-white/55 text-base leading-relaxed mb-12">
              {subheadline}
            </p>

            <div className="space-y-6 mb-12">
              {[
                { icon: 'ri-phone-fill', label: 'Call or Text', value: '346-363-8363', href: 'tel:3463638363' },
                { icon: 'ri-map-pin-line', label: 'Location', value: '8200 Jet Pilot St, Houston TX 77075', href: null },
                { icon: 'ri-time-line', label: 'Availability', value: '7 Days a Week — Houston & Surrounding Areas', href: null },
              ].map((item, i) => (
                <div key={i} className="flex gap-4 items-start">
                  <span className="w-10 h-10 flex items-center justify-center border border-[#BF9448]/25 flex-shrink-0">
                    <i className={`${item.icon} text-[#BF9448] text-base`}></i>
                  </span>
                  <div>
                    <div className="font-sans text-white/35 text-[10px] uppercase tracking-[0.18em]">{item.label}</div>
                    {item.href ? (
                      <a
                        href={item.href}
                        className="font-sans text-white font-semibold text-base mt-0.5 hover:text-[#BF9448] transition-colors duration-200 cursor-pointer block"
                      >
                        {item.value}
                      </a>
                    ) : (
                      <div className="font-sans text-white font-medium text-sm mt-0.5">{item.value}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-10 pt-8 border-t border-white/10">
              {[
                { value: '16+', label: 'Years Serving Houston' },
                { value: '5.0★', label: 'Google Rating' },
                { value: '100%', label: 'Satisfaction Guarantee' },
              ].map((stat, i) => (
                <div key={i}>
                  <div className="font-serif text-[#BF9448] font-semibold text-2xl leading-none">{stat.value}</div>
                  <div className="font-sans text-white/35 text-[10px] uppercase tracking-widest mt-1.5">{stat.label}</div>
                </div>
              ))}
            </div>
          </RevealOnScroll>

          {/* Right — form */}
          <RevealOnScroll direction="right">
            <div className="bg-white/5 border border-white/10 backdrop-blur-sm p-8 lg:p-10">
              <h3 className="font-serif text-white text-xl font-semibold mb-1">
                Get Your Free Instant Quote
              </h3>
              <p className="font-sans text-white/35 text-sm mb-7">
                Serving Houston TX 77075 &amp; all surrounding Greater Houston areas.
              </p>

              {formState === 'success' ? (
                <div className="text-center py-14">
                  <span className="w-16 h-16 flex items-center justify-center border border-[#BF9448]/30 mx-auto mb-5">
                    <i className="ri-checkbox-circle-line text-[#BF9448] text-4xl"></i>
                  </span>
                  <h4 className="font-serif text-white text-xl font-semibold mb-3">Request Received!</h4>
                  <p className="font-sans text-white/45 text-sm">
                    We&rsquo;ll be in touch shortly. Questions? Call 346-363-8363.
                  </p>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  method="POST"
                  data-readdy-form
                  className="space-y-4"
                >
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block font-sans text-[10px] text-white/40 uppercase tracking-[0.18em] mb-2">
                        First Name *
                      </label>
                      <input
                        type="text"
                        name="name"
                        required
                        placeholder="First name"
                        className="w-full bg-white/5 border border-white/10 focus:border-[#BF9448]/50 text-white text-sm font-sans px-4 py-3 outline-none placeholder:text-white/20 transition-colors duration-200"
                      />
                    </div>
                    <div>
                      <label className="block font-sans text-[10px] text-white/40 uppercase tracking-[0.18em] mb-2">
                        Last Name *
                      </label>
                      <input
                        type="text"
                        name="last_name"
                        required
                        placeholder="Last name"
                        className="w-full bg-white/5 border border-white/10 focus:border-[#BF9448]/50 text-white text-sm font-sans px-4 py-3 outline-none placeholder:text-white/20 transition-colors duration-200"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block font-sans text-[10px] text-white/40 uppercase tracking-[0.18em] mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="your@email.com"
                      className="w-full bg-white/5 border border-white/10 focus:border-[#BF9448]/50 text-white text-sm font-sans px-4 py-3 outline-none placeholder:text-white/20 transition-colors duration-200"
                    />
                  </div>
                  <div>
                    <label className="block font-sans text-[10px] text-white/40 uppercase tracking-[0.18em] mb-2">
                      Phone *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      placeholder="(###) ###-####"
                      className="w-full bg-white/5 border border-white/10 focus:border-[#BF9448]/50 text-white text-sm font-sans px-4 py-3 outline-none placeholder:text-white/20 transition-colors duration-200"
                    />
                  </div>
                  <div>
                    <label className="block font-sans text-[10px] text-white/40 uppercase tracking-[0.18em] mb-2">
                      Service Needed
                    </label>
                    <select
                      name="service"
                      className="w-full bg-white/5 border border-white/10 focus:border-[#BF9448]/50 text-white/80 text-sm font-sans px-4 py-3 outline-none transition-colors duration-200 cursor-pointer"
                    >
                      <option value="" className="bg-[#1A1815]">Select a service...</option>
                      {serviceOptions.map((s) => (
                        <option key={s} value={s} className="bg-[#1A1815]">{s}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block font-sans text-[10px] text-white/40 uppercase tracking-[0.18em] mb-2">
                      Project Description
                    </label>
                    <textarea
                      name="message"
                      rows={3}
                      maxLength={500}
                      placeholder="Tell us what needs to be done..."
                      onChange={(e) => setCharCount(e.target.value.length)}
                      className="w-full bg-white/5 border border-white/10 focus:border-[#BF9448]/50 text-white text-sm font-sans px-4 py-3 outline-none placeholder:text-white/20 transition-colors duration-200 resize-none"
                    />
                    <div className="text-right font-sans text-white/20 text-xs mt-1">{charCount}/500</div>
                  </div>

                  {formState === 'error' && (
                    <p className="font-sans text-red-400 text-sm">
                      Something went wrong. Please call us at 346-363-8363.
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={formState === 'submitting'}
                    className="w-full bg-[#BF9448] hover:bg-[#D4AB5E] disabled:opacity-60 text-white font-sans font-semibold text-xs py-4 transition-colors duration-200 whitespace-nowrap cursor-pointer tracking-[0.15em] uppercase mt-2"
                  >
                    {formState === 'submitting' ? 'Sending...' : 'Request Free Quote'}
                  </button>
                </form>
              )}
            </div>
          </RevealOnScroll>

        </div>
      </div>
    </section>
  );
}
